import type { GatedLaunchField } from './visibility';
import { getHiddenLaunchFieldIds } from './visibility';

/**
 * A launch field that takes several choices: a template checkbox field. It is
 * a picklist whose answer is the chosen values joined by commas. A choice
 * value is an option's `choiceRefId`, a UUID, so it never contains a comma.
 */
export interface LaunchFieldSelection {
  /** Fewest choices once any are made. Leaving the field unanswered is always allowed. */
  min: number;
  /** Most choices allowed. Null for no limit. */
  max?: number | null;
}

interface SelectableField {
  selection?: LaunchFieldSelection | null;
}

const SEPARATOR = ',';

/** Why a launch form will not submit while a checkbox field's choices break its limits. */
export const LAUNCH_SELECTION_DISABLED_REASON = 'Fix the number of options selected';

/** The chosen values in a multi-choice answer, in order, without blanks or repeats. */
export function parseLaunchSelectionValue(value: string | null | undefined): string[] {
  if (!value) return [];
  return [
    ...new Set(
      value
        .split(SEPARATOR)
        .map(part => part.trim())
        .filter(Boolean)
    ),
  ];
}

export function formatLaunchSelectionValue(values: readonly string[]): string {
  return [...new Set(values)].join(SEPARATOR);
}

/**
 * Why a number of choices breaks the field's limits, or null when it fits.
 * Nothing chosen always fits: launch questions are optional, and the person
 * filling in the document can still answer it there.
 */
export function getLaunchSelectionError(
  selection: LaunchFieldSelection,
  count: number
): string | null {
  if (count === 0) return null;
  if (count < selection.min) return `Select at least ${pluralizeOptions(selection.min)}.`;
  if (selection.max != null && count > selection.max)
    return `Select at most ${pluralizeOptions(selection.max)}.`;
  return null;
}

/** A short line under the field that states its limits, or null when there are none. */
export function getLaunchSelectionHint(selection: LaunchFieldSelection): string | null {
  const { max, min } = selection;
  if (max != null && min > 0)
    return min === max
      ? `Select ${pluralizeOptions(min)}.`
      : `Select ${min} to ${pluralizeOptions(max)}.`;
  if (min > 0) return `Select at least ${pluralizeOptions(min)}.`;
  if (max != null) return `Select up to ${pluralizeOptions(max)}.`;
  return null;
}

/** Whether another choice can be added without passing the field's maximum. */
export function getCanAddLaunchSelection(selection: LaunchFieldSelection, count: number): boolean {
  return selection.max == null || count < selection.max;
}

/**
 * Whether any visible multi-choice field holds a number of choices its limits
 * do not allow. A hidden field does not count: its answer is not sent.
 */
export function getHasLaunchSelectionError<TField extends GatedLaunchField & SelectableField>(
  fields: readonly TField[],
  valueByFieldId: ReadonlyMap<string, string>
): boolean {
  const hidden = getHiddenLaunchFieldIds([...fields], valueByFieldId);
  return fields.some(field => {
    if (!field.selection || hidden.has(field.fieldId)) return false;
    const count = parseLaunchSelectionValue(valueByFieldId.get(field.fieldId)).length;
    return getLaunchSelectionError(field.selection, count) !== null;
  });
}

function pluralizeOptions(count: number): string {
  return `${count} ${count === 1 ? 'option' : 'options'}`;
}
